import type {
  TBriefing,
  TBriefingsParams,
  TResponse,
} from '~/src/types/briefings'
import getBriefingBySlug from '~/queries/get-briefing-by-slug'
import getBriefingsQuery from '~/queries/get-briefings'
import { logError } from '~/utils/log-error'

import type { TGeneral } from '~/src/types/common'
import type { TMarketSlug } from '~/src/types/markets'

export const useBriefings = () => {
  const { $bmAuth } = useNuxtApp()

  const getBriefings = async ({
    market,
  }: TBriefingsParams): Promise<TBriefing[]> => {
    try {
      const gqlClient = $bmAuth.getGraphqlClient()
      const response = await gqlClient.query({
        query: getBriefingsQuery(market),
      })

      if (response?.errors || !response?.data) {
        logError(response?.errors)
        return []
      }

      return getAdaptedBriefings(response, market)
    } catch (error) {
      logError(error)
      return []
    }
  }

  const getBriefing = async (params: {
    market: TMarketSlug
    category: string
    slug: string
  }): Promise<TResponse<TBriefing>> => {
    try {
      const gqlClient = $bmAuth.getGraphqlClient()
      const response = await gqlClient.query({
        query: getBriefingBySlug(params),
      })

      if (response?.errors || !response?.data) {
        return {
          error: {
            message: 'Briefing not found',
            code: 'NOT_FOUND',
            httpStatus: 404,
          },
          data: null,
        }
      }

      const [post]: TGeneral[] = Object.values(response.data)
      const briefing = getAdaptedBriefing(post, params.market, params.slug)

      return {
        data: briefing,
        error: null,
      }
    } catch (error) {
      logError(error)
      return {
        error: null,
        data: null,
      }
    }
  }

  return { getBriefings, getBriefing }
}
